import React, { useState } from "react";
import EnterpriseGridCommon from "./Ag-Grid";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { aggridHelpers } from "../../helpers/common/ag-grid-helper";
import { logDataHelperFunctions } from "../../helpers/logdata-helper";

const SlogStatusGrid = ({
  rows,
  isLoading,
  handleRowClick,
  handleWeldNumberChange,
  handleDateFilterChange,
}) => {
  const toggleProjectListState = useSelector(
    (state) => state?.switchThemeReducer?.toggleProjectList
  );

  const toggleProjectDetailsState = useSelector(
    (state) => state?.switchThemeReducer?.toggleProjectDetails
  );

  let heightValue;

  if (!toggleProjectDetailsState && toggleProjectListState) {
    heightValue = -295;
  } else if (toggleProjectDetailsState && !toggleProjectListState) {
    heightValue = -250;
  } else if (!toggleProjectDetailsState && !toggleProjectListState) {
    heightValue = -175;
  } else if (toggleProjectDetailsState && toggleProjectListState) {
    heightValue = -370;
  }

  const { t } = useTranslation();

  const [columnDefs] = useState([
    {
      headerName: t("sLogData.slNo"),
      field: "sl_no",
      key: "sl_no",
      minWidth: 100,
      pivot: true,
      flex: 1,
      floatingFilter: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        maxNumConditions: 1,
        cellHeight: 30,
      },
      pivotComparator: (a, b) => b.localeCompare(a),
    },
    {
      headerName: t("sLogData.weldNumber"),
      field: "weldNumber",
      key: "weldNumber",
      minWidth: 100,
      pivot: true,
      flex: 1,
      floatingFilter: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        maxNumConditions: 1,
      },

      pivotComparator: (a, b) => b.localeCompare(a),
    },
    {
      headerName: t("sLogData.status"),
      field: "status",
      key: "status",
      minWidth: 100,
      flex: 1,
      cellRenderer: (params) =>
        logDataHelperFunctions.getIconForStatus(params.value),
      pivot: true,
      floatingFilter: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agSetColumnFilter",
            filterParams: {
              cellHeight: 40,
              cellRenderer: (params) => {
                const status = String(params.value);
                if (status === "(Select All)") {
                  return "(Select All)";
                }
                return logDataHelperFunctions.getIconForStatus(status);
              },
              valueFormatter: (params) => {
                return String(params.value);
              },
            },
          },
        ],
        maxNumConditions: 1,
      },
      processCellForExport: (params) => {
        return params.value
          ? logDataHelperFunctions.getExportValueForStatus(params.value)
          : "";
      },
      pivotComparator: (a, b) => b.localeCompare(a),
    },
    {
      headerName: "Weld Start Time",
      field: "weldStartTime",
      key: "weldStartTime",
      minWidth: 150,
      flex: 1,
      pivot: true,
      floatingFilter: true,
      filter: "agMultiColumnFilter",
      cellRenderer: (params) => {
        return Number(params.value) > 0
          ? logDataHelperFunctions.formatWeldStartTime(
              params.value,
              false,
              false,
              true
            )
          : "-";
      },
      processCellForExport: (params) => {
        return Number(params.value) > 0
          ? logDataHelperFunctions.formatWeldStartTime(
              params.value,
              false,
              false,
              true
            )
          : "-";
      },
      pivotComparator: (a, b) => b.localeCompare(a),
      filterParams: {
        maxNumConditions: 1,
        filters: [
          {
            filter: "agDateColumnFilter",
            filterParams: {
              comparator: (filterLocalDateAtMidnight, cellValue) => {
                return aggridHelpers.dateFilter(
                  filterLocalDateAtMidnight,
                  cellValue
                );
              },
              maxNumConditions: 1,
              inRangeInclusive: true,
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              cellRenderer: (params) => {
                const value = String(params.value);
                if (value === "(Select All)") {
                  return "(Select All)";
                }
                return Number(params.value) > 0
                  ? logDataHelperFunctions.formatWeldStartTime(
                      params.value,
                      false,
                      false,
                      true
                    )
                  : "-";
              },
              valueFormatter: (params) => {
                return String(params.value);
              },
              comparator: (filterLocalDateAtMidnight, cellValue) => {
                return aggridHelpers.dateFilter(
                  filterLocalDateAtMidnight,
                  cellValue
                );
              },
            },
          },
        ],
      },
    },
    {
      headerName: t("sLogData.weldTime"),
      field: "weld_time",
      key: "weld_time",
      minWidth: 100,
      flex: 1,
      cellRenderer: (params) => {
        const value = params.value;
        if (
          value === 0 ||
          value === "0" ||
          value === "0.00" ||
          value === null ||
          value === undefined
        ) {
          return "-";
        }
        return value;
      },
      processCellForExport: (params) => {
        const value = params.value;
        if (
          value === 0 ||
          value === "0" ||
          value === "0.00" ||
          value === null ||
          value === undefined
        ) {
          return "-";
        }
        return value;
      },
      pivot: true,
      floatingFilter: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        maxNumConditions: 1,
      },
      pivotComparator: (a, b) => b.localeCompare(a),
    },
  ]);

  const onRowClicked = (params) => {
    handleRowClick(params.data);
  };

  const handleFilterChanged = (params) => {
    console.log("Filter changed params:", params);

    if (params.weldNumber) {
      const filterModels = params.weldNumber?.filterModels;
      let filteredWeldNumbers = null;

      if (filterModels) {
        // Check all filter models dynamically for text and set filters
        for (const filterModel of filterModels) {
          if (filterModel?.filterType === "text" && filterModel?.filter) {
            // Handle text filter type
            const searchText = filterModel.filter.toLowerCase();
            const matchingWeldNumbers = rows
              .filter(
                (row) =>
                  row.weldNumber &&
                  row.weldNumber.toLowerCase().includes(searchText)
              )
              .map((row) => row.weldNumber);
            filteredWeldNumbers = matchingWeldNumbers;
            break;
          } else if (filterModel?.filterType === "set" && filterModel?.values) {
            // Handle set filter type
            filteredWeldNumbers = filterModel.values;
            break;
          }
        }

        // Legacy support - check for filter properties without filterType
        if (!filteredWeldNumbers) {
          for (const filterModel of filterModels) {
            if (filterModel?.filter) {
              filteredWeldNumbers = [filterModel.filter];
              break;
            } else if (filterModel?.values) {
              filteredWeldNumbers = filterModel.values;
              break;
            }
          }
        }
      }

      console.log("Filtered weld numbers:", filteredWeldNumbers);
      if (handleWeldNumberChange && filteredWeldNumbers) {
        handleWeldNumberChange(filteredWeldNumbers);
      }
    }

    if (params.weldStartTime) {
      let fromDate = params.weldStartTime?.filterModels?.[0]?.dateFrom;
      let toDate = params.weldStartTime?.filterModels?.[0]?.dateTo;

      fromDate = fromDate ? new Date(fromDate).valueOf() : null;
      toDate = toDate ? new Date(toDate).valueOf() : null;

      if (handleDateFilterChange) {
        handleDateFilterChange(fromDate, toDate);
      }
    }
  };

  const processCellCallback = (params) => {
    if (params?.column?.colDef?.processCellForExport) {
      return params.column.colDef.processCellForExport(params);
    }
    return params.value;
  };

  return (
    <div
      className="ag-theme-alpine slog-grid-container"
      style={{ width: "100%" }}
    >
      <EnterpriseGridCommon
        rows={rows}
        columnDefs={columnDefs}
        isLoading={isLoading}
        onRowClicked={onRowClicked}
        exportFileName={"Slog-status"}
        handleFilterChanged={handleFilterChanged}
        processCellCallback={processCellCallback}
        heightValue={heightValue}
        slogData={true}
      />
    </div>
  );
};

export default SlogStatusGrid;
